// ---------------------------------------------------------------------------
// Shared serialized write channel for the per-task immediate-write PUTs.
//
// Every surface that persists the same task field-group (the Overview slider,
// the header status pill, the Overview lifecycle buttons — or the Locations
// observer) funnels through ONE channel per (endpoint, site, schedule, task)
// key, so a single debounce timer / pending value / in-flight request exists no
// matter how many components are mounted. This is what makes the writes safe:
//
// - SERIALIZED: at most one request is in flight per key, and the next value is
//   sent only after the previous response lands — out-of-order responses (the
//   classic "server keeps 50%, UI shows 80%" divergence on a slow link) are
//   structurally impossible.
// - COALESCED: values written while one is in flight or debouncing collapse to
//   the latest (optionally via `coalesce`), so a burst of edits costs at most
//   one trailing request.
// - RETRIED, BUT ONLY WHAT MAY YET SUCCEED: a send that failed in transit (a
//   network error, a 5xx, a 408/429 — `isTransientFailure`) is retried with
//   backoff (unless a newer value has superseded it). A refusal (any 4xx) is
//   the server's answer to that value and repeating it repeats the answer, so
//   it gives up at once. `onGiveUp` fires exactly once per abandoned value, with
//   the error that ended it —
//   after the attempts are exhausted, or on the refusal. The whole-doc Save
//   deliberately does NOT write these fields for existing tasks (see backend
//   SchedulesRepoPort.save), so the retry here is the only recovery path, and
//   a give-up is final: the caller rolls the value back and says so.
//
// Framework-free (no React, no store) and keyed by opaque strings; entries are
// dropped once idle so the registry stays bounded.
// ---------------------------------------------------------------------------

import { isTransientFailure } from '@d-orca/shared/client/apis';

export interface IImmediateWriteOptions<T> {
  /** Merge a new value into a not-yet-sent pending one (default: replace). */
  coalesce?: (prev: T, next: T) => T;
  debounceMs?: number;
}

interface IChannelEntry<T> {
  pending: T | null;
  timer: number | null;
  inFlight: boolean;
  attempts: number;
  send: (value: T) => Promise<unknown>;
  /** Fired with the error that ended the value — the caller words its notice by it. */
  onGiveUp: (error: unknown) => void;
  coalesce: ((prev: T, next: T) => T) | null;
  debounceMs: number;
}

const DEFAULT_DEBOUNCE_MS = 400;
const MAX_ATTEMPTS = 3;
const RETRY_BASE_MS = 1000;

const channels = new Map<string, IChannelEntry<unknown>>();

/** Stable channel key. JSON-encodes the parts so ids can't collide on a separator. */
export function immediateWriteKey(...parts: string[]): string {
  return JSON.stringify(parts);
}

/**
 * Queue `value` for the channel at `key` (debounced). `send`/`onGiveUp` are
 * refreshed on every call, so the latest caller's closures are always used.
 */
export function writeImmediate<T>(
  key: string,
  value: T,
  send: (value: T) => Promise<unknown>,
  onGiveUp: (error: unknown) => void,
  options: IImmediateWriteOptions<T> = {},
): void {
  let entry = channels.get(key) as IChannelEntry<T> | undefined;
  if (!entry) {
    entry = {
      pending: null,
      timer: null,
      inFlight: false,
      attempts: 0,
      send,
      onGiveUp,
      coalesce: options.coalesce ?? null,
      debounceMs: options.debounceMs ?? DEFAULT_DEBOUNCE_MS,
    };
    channels.set(key, entry as IChannelEntry<unknown>);
  }
  entry.send = send;
  entry.onGiveUp = onGiveUp;
  entry.pending = entry.pending !== null && entry.coalesce
    ? entry.coalesce(entry.pending, value)
    : value;
  // A fresh user edit is new intent: restart the retry budget and the debounce.
  entry.attempts = 0;
  if (entry.timer !== null) window.clearTimeout(entry.timer);
  entry.timer = window.setTimeout(() => {
    entry.timer = null;
    drain(key);
  }, entry.debounceMs);
}

/** Send any pending value for `key` now (unmount / task-switch flush). */
export function flushImmediate(key: string): void {
  const entry = channels.get(key);
  if (!entry) return;
  if (entry.timer !== null) {
    window.clearTimeout(entry.timer);
    entry.timer = null;
  }
  drain(key);
}

function drain(key: string): void {
  const entry = channels.get(key);
  // An in-flight request's completion handler re-drains, picking pending up.
  if (!entry || entry.inFlight) return;
  if (entry.pending === null) {
    dropIfIdle(key, entry);
    return;
  }
  const value = entry.pending;
  entry.pending = null;
  entry.inFlight = true;
  entry.send(value).then(
    () => {
      entry.inFlight = false;
      entry.attempts = 0;
      drain(key); // chain: send whatever accrued while this was in flight
    },
    (error: unknown) => {
      entry.inFlight = false;
      if (entry.pending !== null) {
        // A newer value superseded the failed one — send it instead, fresh budget.
        entry.attempts = 0;
        drain(key);
        return;
      }
      entry.attempts += 1;
      if (!isTransientFailure(error) || entry.attempts >= MAX_ATTEMPTS) {
        entry.attempts = 0;
        entry.onGiveUp(error);
        dropIfIdle(key, entry);
        return;
      }
      entry.pending = value;
      entry.timer = window.setTimeout(() => {
        entry.timer = null;
        drain(key);
      }, RETRY_BASE_MS * entry.attempts);
    },
  );
}

function dropIfIdle(key: string, entry: IChannelEntry<unknown>): void {
  if (entry.pending === null && entry.timer === null && !entry.inFlight) {
    channels.delete(key);
  }
}

/** Test-only: clear all channels (and their timers) between test cases. */
export function resetImmediateWriteChannelsForTests(): void {
  for (const entry of channels.values()) {
    if (entry.timer !== null) window.clearTimeout(entry.timer);
  }
  channels.clear();
}
